<template>
  <div class="p-0">
    <div class="flex items-center justify-between mb-6 gap-4">
      <h1 class="text-2xl font-bold text-gray-800">Lotes de Tareas</h1>
      <button
        @click="goToCreateBatch"
        class="px-4 py-2 text-gray-200 bg-blue-600 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
      >
        + Nuevo Lote
      </button>
    </div>

    <!-- Loading -->
    <div v-if="loading" class="py-10">
      <Loading />
    </div>

    <!-- Error -->
    <div v-else-if="error" class="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md">
      {{ error }}
    </div>

    <div v-else>
      <!-- Stats -->
      <div class="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-8 gap-3 mb-5">
        <div class="bg-white border border-gray-200 rounded-lg p-4">
          <div class="text-xs font-semibold text-gray-500 uppercase">Total tareas</div>
          <div class="mt-1 text-2xl font-bold text-indigo-700">{{ totals.total }}</div>
        </div>
        <div
          v-for="col in statusColumns"
          :key="col.key"
          class="bg-white border border-gray-200 rounded-lg p-4"
        >
          <div class="text-xs font-semibold text-gray-500 uppercase">{{ col.label }}</div>
          <div class="mt-1 text-2xl font-bold" :class="col.class">{{ totals.byStatus[col.key] }}</div>
        </div>
      </div>

      <!-- Category Tabs -->
      <div class="flex flex-wrap gap-2 mb-4">
        <button
          @click="activeCategory = ''"
          :class="[
            'px-3 py-1.5 rounded-full text-sm font-medium border transition-colors',
            activeCategory === '' ? 'bg-indigo-600 text-white border-indigo-600' : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50',
          ]"
        >
          Todas
        </button>
        <button
          v-for="c in categories"
          :key="c.id"
          @click="activeCategory = c.id"
          :class="[
            'px-3 py-1.5 rounded-full text-sm font-medium border transition-colors',
            activeCategory === c.id ? 'bg-indigo-600 text-white border-indigo-600' : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50',
          ]"
        >
          {{ c.name }}
        </button>
      </div>

      <!-- Table -->
      <div class="overflow-x-auto rounded-lg border border-gray-200">
        <table class="min-w-full bg-white">
          <thead>
            <tr class="bg-gray-50">
              <th class="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase border-b border-gray-200">
                Título
              </th>
              <th class="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase border-b border-gray-200">
                Categoría
              </th>
              <th class="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase border-b border-gray-200">
                Fecha creación
              </th>
              <th class="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase border-b border-gray-200">
                Total
              </th>
              <th
                v-for="col in statusColumns"
                :key="col.key"
                class="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase border-b border-gray-200"
              >
                {{ col.label }}
              </th>
              <th class="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase border-b border-gray-200">
                Acciones
              </th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="b in filteredBatches"
              :key="b.id"
              class="hover:bg-gray-50 border-b border-gray-100"
            >
              <td class="px-4 py-3 text-sm font-medium text-gray-900 whitespace-nowrap">
                {{ b.title || '-' }}
              </td>
              <td class="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">
                {{ batchCategoryName(b) }}
              </td>
              <td class="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">
                {{ formatDate(b.created_at || b.createdAt) }}
              </td>
              <td class="px-4 py-3 text-sm text-gray-900 whitespace-nowrap">
                {{ b.statsTotal }}
              </td>
              <td
                v-for="col in statusColumns"
                :key="col.key"
                class="px-4 py-3 text-sm whitespace-nowrap"
                :class="col.class"
              >
                {{ b.statusCounts?.[col.key] ?? 0 }}
              </td>
              <td class="px-4 py-3 whitespace-nowrap">
                <button
                  @click="openBatchTasks(b.id)"
                  class="px-3 py-1.5 text-sm bg-gray-800 text-white rounded-md hover:bg-gray-700"
                >
                  Ver tareas
                </button>
              </td>
            </tr>
            <tr v-if="filteredBatches.length === 0">
              <td :colspan="tableColspan" class="px-4 py-8 text-center text-sm text-gray-500">
                No hay lotes para la categoría seleccionada.
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </div>
</template>

<script>
const STATUS_COLUMNS = [
  { key: 'pendiente', label: 'Pendiente', class: 'text-yellow-700' },
  { key: 'en_progreso', label: 'En progreso', class: 'text-blue-700' },
  { key: 'bloqueada', label: 'Bloqueada', class: 'text-purple-700' },
  { key: 'revisar', label: 'Revisar', class: 'text-orange-700' },
  { key: 'completada', label: 'Completada', class: 'text-green-700' },
  { key: 'cancelada', label: 'Cancelada', class: 'text-gray-600' },
  { key: 'vencida', label: 'Vencida', class: 'text-red-700' },
];

const STATUS_KEYS = STATUS_COLUMNS.map((c) => c.key);

export default {
  name: 'TaskBatchesView',
  data() {
    return {
      loading: false,
      error: null,
      batches: [],
      categories: [],
      activeCategory: '',
      statusColumns: STATUS_COLUMNS,
    };
  },
  computed: {
    tableColspan() {
      return 4 + this.statusColumns.length + 1;
    },
    filteredBatches() {
      if (!this.activeCategory) return this.batches;
      return this.batches.filter((b) => String(b.category_id || b.category?.id) === String(this.activeCategory));
    },
    totals() {
      const byStatus = Object.fromEntries(STATUS_KEYS.map((k) => [k, 0]));
      for (const b of this.batches) {
        for (const k of STATUS_KEYS) {
          byStatus[k] += Number(b.statusCounts?.[k] ?? 0);
        }
      }
      return {
        total: this.batches.reduce((acc, b) => acc + (b.statsTotal || 0), 0),
        byStatus,
      };
    },
  },
  created() {
    this.fetchAll();
  },
  methods: {
    unwrapData(resp) {
      return resp?.data?.data ?? resp?.data?.data?.data ?? resp?.data ?? [];
    },
    parseStatusCounts(payload) {
      const raw = payload?.status_counts ?? payload ?? {};
      const counts = Object.fromEntries(STATUS_KEYS.map((k) => [k, Number(raw[k] ?? 0)]));

      const hasAny = STATUS_KEYS.some((k) => counts[k] > 0);
      if (!hasAny && payload) {
        counts.completada = Number(
          payload.completed_count ?? payload.completed ?? payload.completadas ?? 0
        );
        counts.cancelada = Number(
          payload.cancelled_count ?? payload.cancelled ?? payload.canceladas ?? 0
        );
        counts.vencida = Number(payload.overdue_count ?? payload.expired ?? payload.vencidas ?? 0);
        const pendingAgg = Number(payload.pending_count ?? payload.pending ?? payload.pendientes ?? 0);
        if (pendingAgg > 0 && counts.pendiente === 0 && counts.en_progreso === 0 && counts.bloqueada === 0) {
          counts.pendiente = pendingAgg;
        }
      }

      return counts;
    },
    sumStatusCounts(counts) {
      return STATUS_KEYS.reduce((acc, k) => acc + Number(counts[k] ?? 0), 0);
    },
    async fetchAll() {
      this.loading = true;
      this.error = null;
      try {
        const [catsRes, batchesRes] = await Promise.all([
          this.$http.get('/task-categories', { params: { active_only: true } }),
          this.$http.get('/task-batches'),
        ]);
        const catsPayload = this.unwrapData(catsRes);
        const batchesPayload = this.unwrapData(batchesRes);

        this.categories = Array.isArray(catsPayload) ? catsPayload : catsPayload?.data || [];
        const baseBatches = Array.isArray(batchesPayload) ? batchesPayload : batchesPayload?.data || [];

        // Traer estadísticas por lote.
        const enriched = await Promise.all(
          baseBatches.map(async (b) => {
            try {
              const statsRes = await this.$http.get(`/task-batches/${b.id}`);
              const statsPayload = this.unwrapData(statsRes);
              const statusCounts = this.parseStatusCounts(statsPayload);
              const statsTotal =
                Number(statsPayload?.total_tasks) || this.sumStatusCounts(statusCounts);

              return {
                ...b,
                statusCounts,
                statsTotal,
              };
            } catch {
              const emptyCounts = Object.fromEntries(STATUS_KEYS.map((k) => [k, 0]));
              return {
                ...b,
                statusCounts: emptyCounts,
                statsTotal: 0,
              };
            }
          })
        );

        this.batches = enriched;
      } catch (err) {
        this.error = err?.response?.data?.message || err?.message || 'Error al cargar lotes';
      } finally {
        this.loading = false;
      }
    },
    formatDate(dateString) {
      if (!dateString) return '-';
      const d = new Date(dateString);
      if (Number.isNaN(d.getTime())) return String(dateString);
      return d.toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit', year: 'numeric' });
    },
    batchCategoryName(batch) {
      const id = batch?.category_id || batch?.category?.id;
      if (!id) return '-';
      const c = this.categories.find((x) => String(x.id) === String(id));
      return c?.name || '-';
    },
    goToCreateBatch() {
      this.$router.push({ path: '/tasks/create', query: { batch: '1' } });
    },
    openBatchTasks(batchId) {
      if (!batchId) return;
      this.$router.push({ path: '/tasks', query: { batch_id: batchId } });
    },
  },
};
</script>

<style scoped>
</style>

